import React, { useEffect, useState } from 'react'
import { useFormContext, useFieldArray } from 'react-hook-form'
import { SelectInput, useTranslate, useNotify, required, useRefresh, FormDataConsumer, NumberInput, BooleanInput, number, minValue } from 'react-admin'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { productServices } from 'src/services/reservation/product/product.service'
import { Delete, Add } from '@mui/icons-material'
import Button from '@mui/material/Button'
import { Grid, Skeleton } from '@mui/material'
import { JsonInput } from 'src/components/uiElements/react-hook-fields/JsonInputReact'
import { removeProductsByIds } from 'src/components/pages/administration/helper'
import { ConfirmationDialog } from 'src/components/uiElements/styledComponents/ConfirmationDialog'
import { hasPermissionFunction } from 'src/helper/HasPermission'

export const FieldArrayProduct = ({ editMode = true }) => {
  const translate = useTranslate()
  const refresh = useRefresh()
  const notify = useNotify()
  const { control } = useFormContext()
  const [optionsProduct, setOptionsProduct] = useState([])
  const [loading, setLoading] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(null)
  const { fields, append, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'product_by_work_unit_relation', // unique name for your Field Array
    keyName: '_id',
  })

  const getAllProducts = async () => {
    setLoading(true)
    try {
      const allProducts = await productServices.getByAll()
      if (allProducts) {
        setOptionsProduct(allProducts)
        setLoading(false)
        return
      }
      setOptionsProduct([])
      notify(`Products not found`, { type: 'error' })
      setLoading(false)
    } catch (error) {
      setOptionsProduct([])
      notify(`please, refresh page`, { type: 'error' })
      setLoading(false)
    }
  }

  useEffect(() => {
    ;(async () => {
      await getAllProducts()
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (confirmDelete) {
      removeProductsByIds(currentIndex, fields, remove, editMode, refresh, notify, true)
      setOpenModal(false)
      setCurrentIndex(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmDelete])

  const deleteProduct = (originalIndex, currency, remove, editMode) => {
    if (editMode && !('new_register' in currency)) {
      setOpenModal(true)
      setCurrentIndex(originalIndex)
      return
    }
    remove(originalIndex)
  }

  return (
    <>
      <ConfirmationDialog
        title={translate('ra.message.delete_product')}
        buttonConfirmText={translate('ra.action.to_confirm')}
        buttonCancel={translate('ra.action.cancel')}
        setOpen={setOpenModal}
        setConfirm={setConfirmDelete}
        open={openModal}
      />
      {
        fields.map((field, index) => (
          <div
            key={field._id}
            style={{
              width: '100%',
              height: 'auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Grid container spacing={3}>
              <EditInputsWrap label={translate('ra.field.product')} required>
                {loading ? (
                  <Skeleton variant='rectangular' width={'100%'} height={40} />
                ) : (
                  <FormDataConsumer>
                    {({ formData }) => {
                      const options = optionsProduct.filter((elem) => {
                        if (formData.product_by_work_unit_relation[index].product_id === elem.id) {
                          return true
                        }

                        return !formData.product_by_work_unit_relation.find(
                          (product) => product.product_id === elem.id
                        )
                      })
                      return (
                        <SelectInput
                          source={`product_by_work_unit_relation.${index}.product_id`}
                          optionText='long_name'
                          fullWidth
                          choices={editMode && !('new_register' in field) ? optionsProduct : options}
                          label={false}
                          validate={required()}
                          disabled={editMode && !('new_register' in field)}
                        />
                      )
                    }}
                  </FormDataConsumer>
                )}
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.days_to_cancel')}>
                <NumberInput
                  source={`product_by_work_unit_relation.${index}.days_to_cancel`}
                  label={false}
                  fullWidth
                  validate={[number(), minValue(-300)]}
                />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.autocancel')} md={3}>
                <BooleanInput
                  source={`product_by_work_unit_relation.${index}.autocancel`}
                  label={false}
                  fullWidth
                />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.is_active')} md={1}>
                <BooleanInput
                  defaultValue={true}
                  source={`product_by_work_unit_relation.${index}.is_active`}
                  label={false}
                  fullWidth
                />
              </EditInputsWrap>
              { (field.product_relation?.ref_table_detail === 'hotel_detail' &&
                hasPermissionFunction('item','rebooker_view')) &&
                <>
                  <EditInputsWrap label={translate('ra.field.disable_rebooker')} xs={12} md={6} lg={6}>
                    <BooleanInput
                      source={`product_by_work_unit_relation.${index}.rebooker_disabled`}
                      label={false}
                      fullWidth
                    />
                  </EditInputsWrap>
                  <EditInputsWrap label={translate('ra.field.days_to_rebook')} xs={12} md={6} lg={6}>
                    <NumberInput
                      source={`product_by_work_unit_relation.${index}.rebooker_days`}
                      label={false}
                      fullWidth
                      validate={[number(), minValue(-300)]}
                    />
                  </EditInputsWrap>
                </>
              }
              <EditInputsWrap label={translate('ra.field.steps')} md={6}>
                <JsonInput source={`product_by_work_unit_relation.${index}.steps`} />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.config')} md={6}>
                <JsonInput source={`product_by_work_unit_relation.${index}.config`} />
              </EditInputsWrap>
            </Grid>
            <Grid container spacing={3} justifyContent='flex-end' className='addItemSeparator'>
              <Grid item xs={12} sm={12} className='grid' style={{ marginTop: 10 }}>
                <Button
                  onClick={() => deleteProduct(index, field, remove, editMode)}
                  startIcon={<Delete />}
                  color='error'
                >
                  {translate('ra.action.delete')}
                </Button>
              </Grid>
            </Grid>
          </div>
        ))
      }
      <Button
        startIcon={<Add />}
        onClick={() => {
          append({
            config: null,
            product_id: null,
            steps: null,
            new_register: true,
          })
        }}
      >
        {translate('ra.action.add_specific', {
          name: translate('ra.field.product'),
        })}
      </Button>
    </>
  )
}
